import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "./FeedbackPage.css";
import api from "../components/api";

export default function FeedbackPage() {
  const navigate = useNavigate();
  const [topic, setTopic] = useState("");
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const topicOptions = [
    "Share feedback",
    "Support",
    "I found a mistake"
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!topic) {
      setError("Please select a topic");
      return;
    }
    
    if (!message.trim()) {
      setError("Please enter your message");
      return;
    }
    
    if (message.length > 500) {
      setError("Message must be 500 characters or less");
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      console.log("Submitting feedback:", { topic, message: message.trim() });
      
      // Use the working combination: /add-products/ with multipart/form-data
      const formData = new FormData();
      formData.append('topic', topic);
      formData.append('message', message.trim());
      
      const response = await api.post('/add-products/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.status === 201) {
        setSuccess(true);
        setTopic("");
        setMessage("");
      } else {
        throw new Error("Unexpected response status");
      }
    } catch (error) {
      console.error("Error submitting feedback:", error);
      
      // More specific error messages
      if (error.response?.status === 401) {
        setError("Please log in to submit feedback.");
      } else if (error.response?.status === 400) {
        setError("Invalid data. Please check your input and try again.");
      } else if (error.response?.status === 404) {
        setError("Feedback endpoint not found. Please contact support.");
      } else if (error.response?.status === 500) {
        setError("Server error. Please try again later.");
      } else if (error.response?.data?.message) {
        setError(error.response.data.message);
      } else if (error.response?.data?.detail) {
        setError(error.response.data.detail);
      } else {
        setError("Failed to submit feedback. Please try again or contact us directly at hello@curu.com.au");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  if (success) {
    return (
      <div className="container my-5">
        <div className="row justify-content-center">
          <div className="col-12 col-lg-11 col-xl-10">
            <div className="feedback-card card border-0 shadow-sm">
              <div className="card-body p-4 p-md-5 text-center">
                <div className="success-icon mb-4">
                  <div className="celebration-badge">
                    <div className="badge-outer">
                      <div className="badge-inner">
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M9 12l2 2 4-4" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                    </div>
                    <div className="confetti">
                      <div className="confetti-piece confetti-1"></div>
                      <div className="confetti-piece confetti-2"></div>
                      <div className="confetti-piece confetti-3"></div>
                      <div className="confetti-piece confetti-4"></div>
                      <div className="confetti-piece confetti-5"></div>
                      <div className="confetti-piece confetti-6"></div>
                    </div>
                  </div>
                </div>
                <h1 className="display-5 fw-bold mb-3 text-dark">Message Sent!</h1>
                <p className="text-muted mb-4 fs-5">
                  We've got your message. We'll be in touch real soon. Thanks a bunch!
                </p>
                <button 
                  className="btn btn-primary btn-lg px-5"
                  onClick={() => navigate(-1)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="feedback-page-container">
      <div className="feedback-card card border-0 shadow-sm">
            <div className="card-body p-4 p-md-5">
              <div className="d-flex align-items-center mb-4">
                <button 
                  className="btn btn-link p-0 me-3"
                  onClick={handleCancel}
                  style={{ fontSize: '1.5rem' }}
                >
                  ←
                </button>
                <h1 className="display-6 fw-bold mb-0 text-dark">Contact Us</h1>
              </div>
              
              <p className="text-muted mb-4">
                Let us know what's on your mind. We love to hear from you.
              </p>

              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <label htmlFor="topic" className="form-label fw-semibold">
                    What it is about?
                  </label>
                  <select
                    id="topic"
                    className="form-select"
                    value={topic}
                    onChange={(e) => setTopic(e.target.value)}
                    required
                  >
                    <option value="">Choose</option>
                    {topicOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-4">
                  <label htmlFor="message" className="form-label fw-semibold">
                    Tell us more
                  </label>
                  <textarea
                    id="message"
                    className="form-control feedback-textarea"
                    rows="6"
                    placeholder="Your Message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    maxLength="500"
                    required
                  />
                  <div className="text-end mt-1">
                    <small className="text-muted">{message.length}/500</small>
                  </div>
                </div>

                {error && (
                  <div className="alert alert-danger" role="alert">
                    {error}
                  </div>
                )}

                <div className="d-flex gap-3">
                  <button
                    type="button"
                    className="btn btn-outline-secondary flex-fill"
                    onClick={handleCancel}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary flex-fill"
                    disabled={isSubmitting || !topic || !message.trim()}
                  >
                    {isSubmitting ? "Sending..." : "Send"}
                  </button>
                </div>
              </form>
            </div>
          </div>
    </div>
  );
}
